/* Encodes and reads compact price tables without changing prices or metadata.
 * Shared by the admin tools and assets/framivo-price-table.js in the theme.
 */
var FramivoPriceTables = (function () {
  'use strict';

  var encoding = 'palette-base64-v1';
  var alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  var decodedTables = new WeakMap();

  function invalidTable() {
    throw new Error('invalid_price_table');
  }

  function byteLength(text) {
    return encodeURIComponent(text).replace(/%[A-F\d]{2}|./g, 'x').length;
  }

  function encodeBytes(bytes) {
    var result = '';
    for (var position = 0; position < bytes.length; position += 3) {
      var first = bytes[position];
      var second = bytes[position + 1];
      var third = bytes[position + 2];
      result += alphabet[first >> 2];
      result += alphabet[((first & 3) << 4) | ((second || 0) >> 4)];
      result += second === undefined ? '=' : alphabet[((second & 15) << 2) | ((third || 0) >> 6)];
      result += third === undefined ? '=' : alphabet[third & 63];
    }
    return result;
  }

  function decodeBytes(text) {
    if (typeof text !== 'string' || text.length % 4 !== 0
        || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(text)) invalidTable();
    var bytes = [];
    for (var position = 0; position < text.length; position += 4) {
      var first = alphabet.indexOf(text[position]);
      var second = alphabet.indexOf(text[position + 1]);
      var third = alphabet.indexOf(text[position + 2]);
      var fourth = alphabet.indexOf(text[position + 3]);
      bytes.push((first << 2) | (second >> 4));
      if (third >= 0) bytes.push(((second & 15) << 4) | (third >> 2));
      if (fourth >= 0) bytes.push(((third & 3) << 6) | fourth);
    }
    if (encodeBytes(bytes) !== text) invalidTable();
    return bytes;
  }

  function validAxis(axis) {
    if (!axis || !Array.isArray(axis.values) || !axis.values.length || axis.values.length > 60) return false;
    return axis.values.every(function (value, position) {
      return typeof value === 'number' && isFinite(value)
        && (position === 0 || value > axis.values[position - 1]);
    });
  }

  function validPrice(value) {
    return value === null || (typeof value === 'number' && isFinite(value) && !Object.is(value, -0));
  }

  function sameValue(first, second) {
    if (first === second) return true;
    if (!first || !second || typeof first !== 'object' || typeof second !== 'object'
        || Array.isArray(first) !== Array.isArray(second)) return false;
    var firstKeys = Object.keys(first).sort();
    var secondKeys = Object.keys(second).sort();
    return firstKeys.length === secondKeys.length && firstKeys.every(function (key, position) {
      return key === secondKeys[position] && sameValue(first[key], second[key]);
    });
  }

  function decodePriceTable(table) {
    if (!table || !Object.prototype.hasOwnProperty.call(table, 'price_encoding')) return table;
    if (decodedTables.has(table)) return decodedTables.get(table);
    if (table.price_encoding !== encoding || Object.prototype.hasOwnProperty.call(table, 'prices')
        || !validAxis(table.width) || !validAxis(table.second)
        || !Array.isArray(table.price_values) || !table.price_values.length
        || table.price_values.length > 3600 || !table.price_values.every(validPrice)
        || ![1, 2].includes(table.price_index_bytes)
        || (table.price_index_bytes === 1 && table.price_values.length > 256)
        || !Array.isArray(table.price_rows) || table.price_rows.length !== table.second.values.length) invalidTable();
    var prices = table.price_rows.map(function (row) {
      var bytes = decodeBytes(row);
      if (bytes.length !== table.width.values.length * table.price_index_bytes) invalidTable();
      var cells = [];
      for (var position = 0; position < bytes.length; position += table.price_index_bytes) {
        var reference = bytes[position];
        if (table.price_index_bytes === 2) reference = reference * 256 + bytes[position + 1];
        if (reference >= table.price_values.length) invalidTable();
        cells.push(table.price_values[reference]);
      }
      return cells;
    });
    var restored = Object.assign({}, table, { prices: prices });
    delete restored.price_encoding;
    delete restored.price_index_bytes;
    delete restored.price_values;
    delete restored.price_rows;
    decodedTables.set(table, restored);
    return restored;
  }

  function encodePriceTable(source, forceCompact) {
    var table = decodePriceTable(source);
    if (!table || !validAxis(table.width) || !validAxis(table.second)
        || !Array.isArray(table.prices) || table.prices.length !== table.second.values.length
        || !table.prices.every(function (row) {
          return Array.isArray(row) && row.length === table.width.values.length && row.every(validPrice);
        })) invalidTable();
    if (!forceCompact && byteLength(JSON.stringify(table)) <= 9000) return table;
    var values = [];
    var references = new Map();
    table.prices.forEach(function (row) {
      row.forEach(function (value) {
        if (!references.has(value)) {
          references.set(value, values.length);
          values.push(value);
        }
      });
    });
    var indexBytes = values.length <= 256 ? 1 : 2;
    var compact = Object.assign({}, table);
    delete compact.prices;
    compact.price_encoding = encoding;
    compact.price_index_bytes = indexBytes;
    compact.price_values = values;
    compact.price_rows = table.prices.map(function (row) {
      var bytes = [];
      row.forEach(function (value) {
        var reference = references.get(value);
        if (indexBytes === 2) bytes.push(reference >> 8);
        bytes.push(reference & 255);
      });
      return encodeBytes(bytes);
    });
    if (byteLength(JSON.stringify(compact)) > 9900) throw new Error('price_table_too_large');
    // A generated table must reproduce every cell before an upload can use it.
    if (!sameValue(decodePriceTable(JSON.parse(JSON.stringify(compact))), table)) invalidTable();
    return compact;
  }

  return {
    encodePriceTable: encodePriceTable,
    decodePriceTable: decodePriceTable,
    byteLength: byteLength,
    samePriceTable: function (first, second) {
      return sameValue(decodePriceTable(first), decodePriceTable(second));
    }
  };
})();

if (typeof module !== 'undefined' && module.exports) module.exports = FramivoPriceTables;

if (typeof window !== 'undefined') window.FramivoPriceTables = FramivoPriceTables;
